import React, { useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Loader2, ShieldCheck, ArrowLeft, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/sonner";
import api, { formatApiError } from "@/lib/api";

/**
 * Public /admin/reset-password?token=… page.
 *
 * Consumes a one-time token minted by /admin/forgot-password, sets a new
 * password, and redirects to /admin so the user can sign in with the new
 * credential. All admin lockout counters are cleared server-side on
 * success.
 */
export default function AdminResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = useMemo(() => params.get("token") || "", [params]);
    const [pw, setPw] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const tooShort = pw.length > 0 && pw.length < 8;
    const mismatch = confirm.length > 0 && confirm !== pw;
    const submitDisabled = loading || !token || pw.length < 8 || confirm !== pw;

    const submit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            await api.post("/admin/reset-password", { token, password: pw });
            setDone(true);
            toast.success("Password reset. Sign in with your new password.");
            // Give the user a beat to see the success screen, then send them
            // to admin sign-in.
            setTimeout(() => navigate("/admin", { replace: true }), 1800);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <section className="min-h-[80vh] flex items-center bg-brand-navy" data-testid="admin-reset-page">
                <div className="container-page max-w-md">
                    <div className="card-soft p-8">
                        <div className="w-12 h-12 rounded-xl bg-rose-500/10 text-rose-600 flex items-center justify-center">
                            <AlertCircle size={20} />
                        </div>
                        <h1 className="mt-5 font-heading font-bold text-2xl text-brand-navy dark:text-slate-100">
                            Missing reset token
                        </h1>
                        <p className="text-brand-muted text-sm mt-1">
                            This URL doesn&apos;t include a valid reset token. Request a fresh
                            reset link and click the button from the email.
                        </p>
                        <Link
                            to="/admin/forgot-password"
                            data-testid="admin-reset-request-new"
                            className="btn-navy mt-6 inline-block"
                        >
                            Request a new reset link
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="min-h-[80vh] flex items-center bg-brand-navy" data-testid="admin-reset-page">
            <div className="container-page max-w-md">
                <div className="card-soft p-8">
                    <div className="w-12 h-12 rounded-xl bg-brand-navy text-white flex items-center justify-center">
                        {done ? <ShieldCheck size={20} /> : <Lock size={20} />}
                    </div>
                    <h1 className="mt-5 font-heading font-bold text-2xl text-brand-navy dark:text-slate-100">
                        {done ? "Password updated" : "Choose a new admin password"}
                    </h1>
                    <p className="text-brand-muted text-sm mt-1">
                        {done
                            ? "Redirecting you to the admin sign-in…"
                            : "Pick something strong you'll remember. Minimum 8 characters."}
                    </p>

                    {!done && (
                        <form onSubmit={submit} className="mt-6 space-y-4">
                            <div>
                                <Label htmlFor="rp-pw">New password</Label>
                                <Input
                                    id="rp-pw"
                                    type="password"
                                    autoFocus
                                    required
                                    minLength={8}
                                    value={pw}
                                    onChange={(e) => setPw(e.target.value)}
                                    data-testid="admin-reset-pw"
                                    className="mt-2"
                                />
                                {tooShort && (
                                    <p className="text-xs text-rose-500 mt-1">At least 8 characters.</p>
                                )}
                            </div>
                            <div>
                                <Label htmlFor="rp-confirm">Confirm password</Label>
                                <Input
                                    id="rp-confirm"
                                    type="password"
                                    required
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    data-testid="admin-reset-confirm"
                                    className="mt-2"
                                />
                                {mismatch && (
                                    <p className="text-xs text-rose-500 mt-1">Passwords don&apos;t match.</p>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={submitDisabled}
                                data-testid="admin-reset-submit"
                                className="btn-navy w-full disabled:opacity-60"
                            >
                                {loading
                                    ? <><Loader2 size={18} className="mr-2 animate-spin" /> Setting new password…</>
                                    : "Set new password"}
                            </button>
                        </form>
                    )}

                    <Link
                        to="/admin"
                        data-testid="admin-reset-back"
                        className="mt-6 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground underline"
                    >
                        <ArrowLeft size={12} /> Back to admin sign-in
                    </Link>
                </div>
            </div>
        </section>
    );
}
